import { cva, type VariantProps } from 'class-variance-authority';

export const HTTP_BADGE_VARIANTS = ['get', 'post', 'put', 'delete', 'patch'] as const;

export type HttpBadgeVariant = (typeof HTTP_BADGE_VARIANTS)[number];

export const badgeVariants = cva(
  [
    'inline-flex items-center justify-center w-fit max-w-full',
    'rounded-full',
    'leading-none',
    'font-semibold',
  ].join(' '),
  {
    variants: {
      variant: {
        get: 'bg-ui-badge-get-bg text-ui-badge-get border border-ui-badge-get-border uppercase tracking-wide',
        post: 'bg-ui-badge-post-bg text-ui-badge-post border border-ui-badge-post-border uppercase tracking-wide',
        put: 'bg-ui-badge-put-bg text-ui-badge-put border border-ui-badge-put-border uppercase tracking-wide',
        delete: 'bg-ui-badge-delete-bg text-ui-badge-delete border border-ui-badge-delete-border uppercase tracking-wide',
        patch: 'bg-ui-badge-patch-bg text-ui-badge-patch border border-ui-badge-patch-border uppercase tracking-wide',
        primary: 'bg-ui-primary text-ui-primary-foreground border border-ui-badge-primary-border',
        accent: 'bg-ui-badge-accent-bg text-ui-badge-accent border border-ui-badge-accent-border',
        muted: 'bg-ui-badge-muted-bg text-ui-badge-muted border border-ui-badge-muted-border',
        success: 'bg-ui-badge-success-bg text-ui-badge-success border border-ui-badge-success-border',
        warning: 'bg-ui-badge-warning-bg text-ui-badge-warning border border-ui-badge-warning-border',
        danger: 'bg-ui-badge-danger-bg text-ui-badge-danger border border-ui-badge-danger-border',
        nullable: 'bg-ui-badge-warning-bg text-ui-badge-warning border border-ui-badge-warning-border',
      },
      size: {
        sm: 'px-[0.42rem] py-[0.18rem] text-[0.7rem]',
        md: 'px-2.5 py-1 text-xs',
      },
    },
    defaultVariants: {
      variant: 'muted',
      size: 'sm',
    },
  }
);

export type BadgeVariantProps = VariantProps<typeof badgeVariants>;

export function httpMethodBadgeVariant(method: string): BadgeVariantProps['variant'] {
  const normalized = method.toLowerCase();
  return (HTTP_BADGE_VARIANTS as readonly string[]).includes(normalized)
    ? (normalized as HttpBadgeVariant)
    : 'muted';
}

export function statusCodeBadgeVariant(statusCode: string | number | null | undefined): BadgeVariantProps['variant'] {
  if (statusCode == null) {
    return 'muted';
  }

  const raw = String(statusCode).trim().toLowerCase();
  if (raw === 'default') {
    return 'muted';
  }

  const rangeMatch = /^([1-5])xx$/.exec(raw);
  if (rangeMatch) {
    const family = Number(rangeMatch[1]);
    if (family === 2) {
      return 'success';
    }
    if (family === 4) {
      return 'warning';
    }
    if (family === 5) {
      return 'danger';
    }
    return 'muted';
  }

  const code = typeof statusCode === 'string' ? Number.parseInt(statusCode, 10) : statusCode;
  if (Number.isNaN(code)) {
    return 'muted';
  }
  if (code >= 200 && code < 300) {
    return 'success';
  }
  if (code >= 400 && code < 500) {
    return 'warning';
  }
  if (code >= 500) {
    return 'danger';
  }
  return 'muted';
}
